import { PERMISSIONS } from "../../constants/permissions";

/**
 * Sidebar menu configuration.
 * Each item can have:
 *  - label: translation key
 *  - path: route path (leaf items)
 *  - icon: icon class
 *  - permissions: array of permission slugs (user needs at least one). null/undefined = always visible
 *  - children: sub-menu items (same shape, no icon needed)
 */
const sidebarMenuConfig = [
  {
    label: "Dashboard",
    path: "/dashboard",
    icon: "ri-dashboard-line",
  },
  {
    label: "Manage Admins",
    icon: "ri-user-settings-line",
    permissions: [
      PERMISSIONS.MANAGE_ADMIN_USERS,
      PERMISSIONS.MANAGE_ADMIN_ROLES,
    ],
    children: [
      {
        label: "Manage Admin users",
        path: "/admin-users",
        permissions: [PERMISSIONS.MANAGE_ADMIN_USERS],
      },
      {
        label: "Manage Admin roles",
        path: "/admin-roles",
        permissions: [PERMISSIONS.MANAGE_ADMIN_ROLES],
      },
    ],
  },
  {
    label: "Manage Coupons",
    path: "/coupons",
    icon: "ri-price-tag-3-line",
    permissions: [PERMISSIONS.MANAGE_COUPONS],
  },
  {
    label: "Manage Tickets",
    path: "/tickets",
    icon: "ri-ticket-line",
    permissions: [PERMISSIONS.MANAGE_TICKETS],
  },
  {
    label: "Support Inbox",
    path: "/inbox",
    icon: "ri-message-3-line",
    permissions: [PERMISSIONS.VIEW_USER_MESSAGES, PERMISSIONS.MANAGE_CHAT],
  },
  {
    label: "Manage Users",
    path: "/user",
    icon: "ri-user-2-line",
    permissions: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.VIEW_USERS],
  },
  {
    label: "Verify User",
    path: "/verify-user",
    icon: "ri-user-2-line",
    permissions: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.VIEW_USERS],
  },
  {
    label: "Account Delete Requests",
    path: "/delete-requests",
    icon: "ri-user-unfollow-line",
    permissions: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.VIEW_USERS],
  },
  {
    label: "Manage Listings",
    path: "/property",
    icon: "ri-profile-line",
    permissions: [PERMISSIONS.MANAGE_LISTINGS, PERMISSIONS.VIEW_LISTINGS],
  },
  {
    label: "In progress listings",
    path: "/property/in-progress",
    icon: "ri-file-edit-line",
    permissions: [PERMISSIONS.MANAGE_LISTINGS, PERMISSIONS.VIEW_LISTINGS],
  },
  {
    label: "Listings Requests",
    path: "/listings-requests",
    icon: "ri-profile-line",
    permissions: [
      PERMISSIONS.MANAGE_LISTINGS,
      PERMISSIONS.VIEW_LISTINGS,
      PERMISSIONS.MANAGE_LISTING_REQUESTS,
    ],
  },
  {
    label: "Edit Requests",
    path: "/edit-requests",
    icon: "ri-edit-line",
    permissions: [
      PERMISSIONS.MANAGE_LISTINGS,
      PERMISSIONS.VIEW_LISTINGS,
      PERMISSIONS.MANAGE_LISTING_REQUESTS,
    ],
  },
  {
    label: "Listing Delete Requests",
    path: "/listing-delete-requests",
    icon: "ri-delete-bin-line",
    permissions: [PERMISSIONS.MANAGE_LISTINGS, PERMISSIONS.VIEW_LISTINGS],
  },
  {
    label: "Manage Reservations",
    path: "/reservation",
    icon: "ri-calendar-2-line",
    permissions: [
      PERMISSIONS.MANAGE_RESERVATIONS,
      PERMISSIONS.VIEW_RESERVATIONS,
    ],
  },
  {
    label: "Manage Sms",
    icon: "ri-wallet-2-line",
    permissions: [PERMISSIONS.VIEW_OTP],
    children: [
      {
        label: "OTP",
        path: "/manage-sms/otp",
        icon: "ri-bill-line",
        permissions: [PERMISSIONS.VIEW_OTP],
      },
    ],
  },
  {
    label: "Payouts",
    icon: "ri-bank-card-line",
    permissions: [PERMISSIONS.MANAGE_PAYOUT],
    children: [
      {
        label: "Payout Dashboard",
        path: "/payouts",
        permissions: [PERMISSIONS.MANAGE_PAYOUT],
      },
      {
        label: "Payout History",
        path: "/payouts/history",
        permissions: [PERMISSIONS.MANAGE_PAYOUT],
      },
    ],
  },
  {
    label: "Manage Fees",
    icon: "ri-wallet-2-line",
    permissions: [
      PERMISSIONS.MANAGE_SITE_SERVICE_FEE,
      PERMISSIONS.MANAGE_SITE_VAT,
      PERMISSIONS.MANAGE_SITE_COMMISSION_FEE,
    ],
    children: [
      {
        label: "Service Fee",
        path: "/service-fee",
        icon: "ri-bill-line",
        permissions: [PERMISSIONS.MANAGE_SITE_SERVICE_FEE],
      },
      {
        label: "VAT",
        path: "/vat",
        icon: "ri-bill-line",
        permissions: [PERMISSIONS.MANAGE_SITE_VAT],
      },
      {
        label: "Commission Fees",
        path: "/commission-fees",
        icon: "ri-bill-line",
        permissions: [PERMISSIONS.MANAGE_SITE_COMMISSION_FEE],
      },
    ],
  },
  {
    label: "Managed By Ghorfatech",
    icon: "ri-wallet-2-line",
    permissions: [PERMISSIONS.MANAGE_MANAGED_BY_GHORFATECH],
    children: [
      {
        label: "Percentage",
        path: "/managed-by-ghorfatech/show/percentage",
        icon: "ri-bill-line",
        permissions: [PERMISSIONS.MANAGE_MANAGED_BY_GHORFATECH],
      },
      {
        label: "Monthly",
        path: "/managed-by-ghorfatech/show/monthly",
        icon: "ri-bill-line",
        permissions: [PERMISSIONS.MANAGE_MANAGED_BY_GHORFATECH],
      },
    ],
  },
  {
    label: "Admin Reviews",
    icon: "ri-user-settings-line",
    permissions: [
      PERMISSIONS.MANAGE_USER_REVIEWS,
      PERMISSIONS.VIEW_USER_REVIEWS,
      PERMISSIONS.MANAGE_ADMIN_REVIEWS,
    ],
    children: [
      {
        label: "Manage Reviews",
        path: "/reviews",
        permissions: [
          PERMISSIONS.MANAGE_USER_REVIEWS,
          PERMISSIONS.VIEW_USER_REVIEWS,
        ],
      },
      {
        label: "Write a review",
        path: "/write-reviews",
        permissions: [PERMISSIONS.MANAGE_ADMIN_REVIEWS],
      },
    ],
  },
  {
    label: "Document verification",
    path: "/document",
    icon: "ri-calendar-2-line",
    permissions: [PERMISSIONS.MANAGE_USER_DOCUMENT_VERIFICATION],
  },
  {
    label: "List settings for step 1",
    icon: "ri-user-settings-line",
    permissions: [PERMISSIONS.MANAGE_LISTING_SETTINGS],
    children: [
      { label: "Room Type", path: "/room-type" },
      { label: "Person Capacity", path: "/person-capacity" },
      { label: "House Type", path: "/house-type" },
      { label: "Bed Rooms", path: "/bedroom" },
      { label: "Beds", path: "/bed" },
      { label: "Bed Type", path: "/bed-type" },
      { label: "Bathrooms", path: "/bathroom" },
      { label: "Bathroom Type", path: "/bathroom-type" },
      { label: "Essential Amenities", path: "/essential" },
      { label: "Safety Amenities", path: "/safety" },
      { label: "Spaces", path: "/space" },
    ],
  },
  {
    label: "List settings for step 3",
    icon: "ri-user-settings-line",
    permissions: [PERMISSIONS.MANAGE_LISTING_SETTINGS],
    children: [
      { label: "House rules", path: "/house-rules" },
      { label: "Minimum nights", path: "/min-nights" },
      { label: "Maximum nights", path: "/max-nights" },
    ],
  },
  {
    label: "Cancellation policy management",
    path: "/cancelation-policy",
    icon: "ri-calendar-2-line",
    permissions: [PERMISSIONS.CANCELLATION_POLICY_MANAGEMENT],
  },
  {
    label: "Home Page Settings",
    icon: "ri-mail-send-line",
    permissions: [
      PERMISSIONS.MANAGE_HOME_PAGE_BANNERS,
      PERMISSIONS.MANAGE_POPULAR_LOCATIONS,
    ],
    children: [
      {
        label: "Image banner",
        path: "/banner-image",
        permissions: [PERMISSIONS.MANAGE_HOME_PAGE_BANNERS],
      },
      {
        label: "Manage popular locations",
        path: "/popular-location",
        permissions: [PERMISSIONS.MANAGE_POPULAR_LOCATIONS],
      },
    ],
  },
];

export default sidebarMenuConfig;
